import { useEffect, useState } from "react";
import { getRoleItem } from "../../../api/content/itemsApi";
import { toUiError } from "../../../errors/uiError";
import { NO_VIEW_PROFILE, viewProfileOf, type AboxViewProfile } from "./aboxViewModel";

/** The role a project binds its view profile to (PG-5, C-B7 AB-5). */
const ABOX_VIEW_ROLE = "abox_view";

const read = new Map<string, Promise<AboxViewProfile>>();

/**
 * The project's profile, read once a session; one it doesn't bind lays nothing out. A read that
 * failed otherwise lays nothing out for now and is tried again by the next view that asks.
 */
function profileOf(projectId: string): Promise<AboxViewProfile> {
  let profile = read.get(projectId);
  if (profile === undefined) {
    profile = getRoleItem(projectId, ABOX_VIEW_ROLE).then(
      (item) => viewProfileOf(item.definition),
      (caught: unknown) => {
        const error = toUiError(caught);
        if (error.kind !== "problem" || error.problem.code !== "resource_not_found") read.delete(projectId);
        return NO_VIEW_PROFILE;
      },
    );
    read.set(projectId, profile);
  }
  return profile;
}

/**
 * The view profile of `projectId`, the project the statements shown belong to, never the one the
 * project menu chose since (Sol review R3); `null` while it loads, so they are laid out once.
 */
export function useAboxViewProfile(projectId: string | null): AboxViewProfile | null {
  const [loaded, setLoaded] = useState<{ readonly projectId: string; readonly profile: AboxViewProfile } | null>(null);
  useEffect(() => {
    if (projectId === null) return;
    let current = true;
    void profileOf(projectId).then((profile) => { if (current) setLoaded({ projectId, profile }); });
    return () => { current = false; };
  }, [projectId]);
  if (projectId === null) return NO_VIEW_PROFILE;
  return loaded !== null && loaded.projectId === projectId ? loaded.profile : null;
}
